import { useState } from "react";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import { HOME } from "@/data/homeContent";

// Performance Calculator — exact inputs (60/25/20/70/20), formula and
// insight ladder from the original script.js. Recalculates on every input.
export default function CalculatorWidget() {
  const c = HOME.calculator;
  const [values, setValues] = useState(() =>
    Object.fromEntries(c.inputs.map((input) => [input.key, input.def])),
  );

  const clamp = (v) => (Number.isFinite(v) && v > 0 ? v : 0);
  const calls = clamp(values.calls);
  const contact = clamp(values.contact);
  const booking = clamp(values.booking);
  const show = clamp(values.show);
  const days = clamp(values.days);

  const convDay = Math.round((calls * contact) / 100);
  const apptDay = Math.round((convDay * booking) / 100);
  const showDay = Math.round((apptDay * show) / 100);
  const convMonth = convDay * days;
  const apptMonth = apptDay * days;
  const showMonth = Math.round((apptMonth * show) / 100);

  let insight;
  if (calls < 40 || days < 15) insight = c.insights.volume;
  else if (booking < 15) insight = c.insights.booking;
  else if (show < 60) insight = c.insights.show;
  else insight = c.insights.strong;

  const fmt = (n) => n.toLocaleString("en-US");
  const outputs = [
    { label: "Conversations / day", value: convDay },
    { label: "Appointments / day", value: apptDay },
    { label: "Shows / day", value: showDay },
    { label: "Monthly conversations", value: convMonth },
    { label: "Monthly booked", value: apptMonth },
    { label: "Monthly shows", value: showMonth },
  ];

  return (
    <section id="calculator" className="bg-ink-950 px-6 py-16 md:py-24" data-testid="home-calculator">
      <div className="mx-auto max-w-4xl">
        <SectionHeader center eyebrow={c.eyebrow} title={c.headline} support={c.support} testId="home-calculator-heading" />
        <Reveal delay={0.15} className="mt-10">
          <div className="rounded-lg border border-line bg-ink-900/60 p-6 md:p-8" data-testid="calculator-panel">
            <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
              {c.inputs.map((input) => (
                <label key={input.key} className="block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-fg-dim">
                    {input.label}
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={input.max || undefined}
                    value={values[input.key]}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [input.key]: e.target.value === "" ? 0 : Number(e.target.value) }))
                    }
                    className="mt-3 w-full rounded-md border border-line bg-ink-950/70 px-4 py-3 font-display text-lg text-fg focus:border-brand focus:outline-none"
                    data-testid={`calc-input-${input.key}`}
                  />
                </label>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 border-t border-line pt-8 sm:grid-cols-3">
              {outputs.map((out) => (
                <div
                  key={out.label}
                  className="rounded-lg border border-line bg-ink-950/60 p-4 text-center"
                  data-testid={`calc-output-${out.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <p className="font-display text-2xl font-semibold text-chrome md:text-3xl">{fmt(out.value)}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-fg-dim">{out.label}</p>
                </div>
              ))}
            </div>

            <p
              className="mt-8 text-center text-sm leading-relaxed text-fg-muted md:text-base"
              data-testid="calc-insight"
              dangerouslySetInnerHTML={{ __html: insight }}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}